import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, PartyPopper, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { UNASSIGNED_CODE, countryName, fmt, fmtDate } from "../lib/constants";
import { fmtTime } from "../lib/hygiene";
import { initials, shortName } from "./OwnerBreakdown";

// why a deal won since the cutoff is not on the board (backend `reason`) + what the deal owner has to fix in HubSpot
export const NO_VENUE_REASON = {
  "untyped companies only": { label: "Company type not set", fix: "Set Company type = Venue on the linked company (or link the right Venue company)." },
  "no company linked": { label: "No company linked", fix: "Associate the Venue company (Company type = Venue) with the deal." },
  "merchant without venues": { label: "Merchant has no venues", fix: "Link the Venue company (Company type = Venue) to the deal or to its Merchant." },
};
const slug = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const chip = (active) => `inline-flex items-center gap-1.5 shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "bg-ink text-page border-ink" : "border-panel-border text-slate-700 hover:border-slate-400"}`;
// one row of filter chips: horizontally scrollable on phones, wrapping from sm up
const chipRow = "flex items-center gap-1.5 overflow-x-auto thin-scroll pb-1 -mb-1 sm:flex-wrap sm:overflow-visible sm:pb-0 sm:mb-0";
const countryLabel = (code) => (code === UNASSIGNED_CODE ? "No country" : countryName(code));

// KPI-panel pill for everyone (TV screens included): deals won since the cutoff that the board cannot count yet
export const NotCountedChip = ({ notCounted, onClick }) => {
  if (!notCounted) return null;
  const n = notCounted.total || 0;
  const since = fmtDate(notCounted.since);
  const title = n
    ? `${fmt(n)} ${n === 1 ? "deal" : "deals"} won since ${since} ${n === 1 ? "is" : "are"} NOT on the board – no Venue company is linked in HubSpot. Waiting for the deal owners to fix the records – click for the list`
    : `Every deal won since ${since} is on the board – no CRM fixes pending`;
  const Icon = n ? AlertTriangle : CheckCircle2;
  return (
    <button
      type="button"
      data-testid="kpi-not-counted"
      data-count={n}
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`inline-flex items-center gap-1.5 tv:gap-[6px] rounded-full border px-2.5 py-1 tv:px-[10px] tv:py-[4px] text-xs tv:text-[13px] font-semibold whitespace-nowrap shadow-sm transition-colors ${n ? "border-amber-400 bg-amber-100 text-amber-900 hover:border-amber-500" : "border-live/40 bg-live/5 text-live"}`}
    >
      <Icon className={`w-3.5 h-3.5 tv:w-[15px] tv:h-[15px] ${n ? "text-amber-700" : "text-live"}`} />
      <span className="num font-bold" data-testid="kpi-not-counted-total">{n ? `+${fmt(n)}` : "0"}</span>
      <span>not counted</span>
      <span className="hidden sm:inline tv:hidden font-normal opacity-80">· {n ? "waiting for CRM fix" : "all on the board"}</span>
    </button>
  );
};

const Row = ({ d }) => {
  const reason = NO_VENUE_REASON[d.reason] || { label: d.reason };
  return (
    <li data-testid={`not-counted-deal-${d.id}`} className="min-w-0 rounded-lg border border-panel-border bg-panel px-3 py-2 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <a href={d.url} target="_blank" rel="noreferrer" data-testid={`not-counted-deal-link-${d.id}`} title="Open the deal in HubSpot" className="font-semibold text-ink truncate hover:text-brand hover:underline inline-flex items-center gap-1 min-w-0">
          <span className="truncate">{d.name}</span><ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
        </a>
        <span className="ml-auto num text-xs text-slate-500 whitespace-nowrap">{d.won_at ? `won ${fmtDate(d.won_at)}` : ""}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1 font-semibold text-ink" data-testid={`not-counted-deal-owner-${d.id}`} title={`Deal owner: ${d.owner}`}>
          <span className="w-5 h-5 rounded-full bg-ink/10 text-[9px] font-bold flex items-center justify-center">{initials(d.owner || "")}</span>{d.owner}
        </span>
        <span className="rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-semibold" data-testid={`not-counted-deal-reason-${d.id}`}>{reason.label}</span>
        {d.country && d.country !== UNASSIGNED_CODE ? <span className="num">{countryName(d.country)}</span> : null}
        {d.companies?.length ? (
          <span className="inline-flex items-center gap-1 flex-wrap">
            set Company type = Venue on
            {d.companies.map((c) => (
              <a key={c.id} href={c.url} target="_blank" rel="noreferrer" data-testid={`not-counted-company-${c.id}`} title="Open the company in HubSpot" className="rounded-full border border-panel-border px-2 py-0.5 text-ink hover:text-brand hover:border-brand">{c.name}</a>
            ))}
          </span>
        ) : d.company ? <span className="text-slate-500">on {d.company}</span> : null}
      </div>
    </li>
  );
};

// venues that got fixed in HubSpot today and are counted now (recorded by the backend at refresh time)
const FixedToday = ({ fixed }) => (
  <div className="mt-3 rounded-xl border border-live/30 bg-live/5 px-3 py-2" data-testid="not-counted-fixed-list">
    <div className="eyebrow text-live mb-1 flex items-center gap-1"><PartyPopper className="w-3.5 h-3.5" /> Fixed today · counted now</div>
    <ul className="grid gap-1 text-xs text-slate-700">
      {fixed.map((f) => (
        <li key={`${f.id}-${f.fixed_at}`} data-testid={`not-counted-fixed-${f.id}`} className="flex items-center gap-2 min-w-0">
          <CheckCircle2 className="w-3.5 h-3.5 text-live shrink-0" />
          <span className="font-semibold text-ink truncate" title={f.name !== f.venue ? `deal: ${f.name}` : undefined}>{f.venue}</span>
          <span className="text-slate-500 whitespace-nowrap">fixed by {shortName(f.owner || "")}{f.country && f.country !== UNASSIGNED_CODE ? ` · ${countryName(f.country)}` : ""}</span>
          <span className="num ml-auto text-slate-400 whitespace-nowrap">{fmtTime(f.fixed_at)}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Dialog: who has to fix what in HubSpot so that these won deals get counted (owner + reason + country + HubSpot link)
export const NotCountedPanel = ({ open, onOpenChange, data, initialCountry = null }) => {
  const [owner, setOwner] = useState(null);
  const [reason, setReason] = useState(null);
  const [country, setCountry] = useState(null);
  useEffect(() => {
    if (open) {
      setCountry(initialCountry || null);
      setOwner(null);
      setReason(null);
    }
  }, [open, initialCountry]);
  const shown = useMemo(
    () => (data?.deals || []).filter((d) => (!owner || d.owner === owner) && (!reason || d.reason === reason) && (!country || (d.country || UNASSIGNED_CODE) === country)),
    [data, owner, reason, country],
  );
  const total = data?.total || 0;
  const fixed = data?.fixed_today || [];
  const since = fmtDate(data?.since);
  const filtered = !!(owner || reason || country);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full p-0 gap-0 overflow-hidden bg-panel text-ink" data-testid="not-counted-panel">
        <div className="flex flex-col min-w-0 min-h-0 max-h-[94vh] sm:max-h-[88vh] gap-3 p-4 sm:p-6 pt-5 sm:pt-6">
          <DialogHeader className="shrink-0 text-left pr-7 space-y-1.5">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-xl font-bold" data-testid="not-counted-title">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${total ? "bg-amber-100 text-amber-700" : "bg-live/10 text-live"}`}>{total ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}</span>
              <span className="min-w-0">
                <span className="num" data-testid="not-counted-total">{fmt(total)}</span> won {total === 1 ? "deal" : "deals"} not counted · waiting for CRM fix
                {fixed.length > 0 && (
                  <span data-testid="not-counted-fixed-today" key={fixed.length} title={fixed.map((f) => `${f.venue} · ${f.owner}`).join("\n")} className="hygiene-celebrate ml-2 inline-flex items-center gap-1 rounded-full bg-live/10 text-live px-2 py-0.5 text-xs font-bold align-middle whitespace-nowrap">
                    <PartyPopper className="w-3.5 h-3.5" /> {fmt(fixed.length)} fixed today
                  </span>
                )}
              </span>
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Deals won since {since} that the board cannot count: no company with <b>Company type = Venue</b> is linked in HubSpot. The deal owner fixes the record – the venue appears on the board after the next refresh (5 min).
            </DialogDescription>
          </DialogHeader>
          {total > 0 && (
            <div className="shrink-0 space-y-2" data-testid="not-counted-filters">
              <div className={chipRow}>
                <span className="eyebrow mr-1 shrink-0">Why</span>
                {Object.entries(data.by_reason || {}).filter(([, n]) => n > 0).map(([key, n]) => (
                  <button key={key} type="button" data-testid={`not-counted-reason-${slug(key)}`} aria-pressed={reason === key} onClick={() => setReason(reason === key ? null : key)} className={chip(reason === key)}>
                    {NO_VENUE_REASON[key]?.label || key} <b className="num">{fmt(n)}</b>{reason === key && <X className="w-3 h-3" />}
                  </button>
                ))}
              </div>
              <div className={chipRow}>
                <span className="eyebrow mr-1 shrink-0">Waiting on</span>
                {(data.by_owner || []).map((o) => (
                  <button key={o.name} type="button" data-testid={`not-counted-owner-${slug(o.name)}`} aria-pressed={owner === o.name} onClick={() => setOwner(owner === o.name ? null : o.name)} className={chip(owner === o.name)} title={o.name}>
                    <span className={`w-5 h-5 rounded-full text-[9px] font-bold flex items-center justify-center ${owner === o.name ? "bg-page/20" : "bg-ink/10"}`}>{initials(o.name)}</span>
                    {shortName(o.name)} <b className="num">{fmt(o.count)}</b>{owner === o.name && <X className="w-3 h-3" />}
                  </button>
                ))}
              </div>
              {Object.keys(data.by_country || {}).length > 0 && (
                <div className={chipRow}>
                  <span className="eyebrow mr-1 shrink-0">Where</span>
                  {Object.entries(data.by_country).map(([code, n]) => (
                    <button key={code} type="button" data-testid={`not-counted-country-${code}`} aria-pressed={country === code} onClick={() => setCountry(country === code ? null : code)} className={chip(country === code)}>
                      {code !== UNASSIGNED_CODE && <span className="num text-[10px] text-slate-400">{code}</span>}{countryLabel(code)} <b className="num">{fmt(n)}</b>{country === code && <X className="w-3 h-3" />}
                    </button>
                  ))}
                </div>
              )}
              {reason && <div className="text-xs text-slate-600 rounded-lg bg-slate-50 border border-panel-border px-3 py-2" data-testid="not-counted-fix"><b>Fix:</b> {NO_VENUE_REASON[reason]?.fix}</div>}
            </div>
          )}
          <div className="flex-1 min-h-[20vh] overflow-y-auto thin-scroll pr-1">
            {total === 0 ? (
              <div className="rounded-xl border border-live/30 bg-live/5 px-4 py-6 text-center text-sm text-slate-700" data-testid="not-counted-empty">Every deal won since {since} is on the board – nothing to fix.</div>
            ) : shown.length === 0 ? (
              <div className="rounded-xl border border-panel-border px-4 py-6 text-center text-sm text-slate-500" data-testid="not-counted-no-match">No deals match this filter.</div>
            ) : (
              <ul className="grid gap-1.5" data-testid="not-counted-list">{shown.map((d) => <Row key={d.id} d={d} />)}</ul>
            )}
            {fixed.length > 0 && !filtered && <FixedToday fixed={fixed} />}
          </div>
          <div className="shrink-0 flex items-center justify-between gap-3 text-xs text-slate-500 border-t border-panel-border pt-3">
            <span data-testid="not-counted-shown" className="whitespace-nowrap">{fmt(shown.length)} of {fmt(total)} shown</span>
            <span className="hidden sm:inline text-right">HubSpot is read-only here – fix the record in HubSpot, the board updates itself.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
